import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Button, Feeds, Icon } from 'lynkco-up-core';
import { Record as FeedRecord } from 'lynkco-up-core/dist/types/components/Feeds';
import { GenericFunction } from 'lynkco-up-core/dist/types/types';

import { Divider } from '../../../components';
import RowDefinitionDetails from '../../../components/RowDefinitionDetails';
import { UserData } from '../../../services/GDPR/types';
import { VerificationScope, VerificationType } from '../../../types';
import { getMarketName } from '../../../utils';
import { getVerificationScopeLabel } from '../../../utils/verificationScope';
import OnfidoVerificationStatus from './OnfidoVerificationStatus';

type ResultsProps = {
  data: UserData;
  verificationScope: VerificationScope;
  selectedVerificationScope: VerificationScope;
  isStale: boolean;
  openRedirectToOnfido: GenericFunction;
};

type VerificationSection = {
  title: string;
  type: VerificationType;
  buildRecords: (data: UserData, verificationType: VerificationType) => FeedRecord[];
};

dayjs.extend(utc);

const dateFormat = 'YYYY-MM-DD HH:mm';

const verificationSections: VerificationSection[] = [
  {
    title: 'Car Sharing drivers license',
    type: VerificationType.CAR_SHARING,
    buildRecords: buildCarSharingRecords,
  },
  {
    title: 'Identity Verification',
    type: VerificationType.IDENTITY_VERIFICATION,
    buildRecords: buildIdentityVerificationRecords,
  },
];

function getIconType(flag?: boolean) {
  return flag ? 'check' : 'close';
}

function getIconBackground(flag?: boolean) {
  return flag ? 'primary' : 'danger';
}

function resolveChecks(data: UserData, verificationScope: VerificationScope) {
  const checks = data.lynkCoApplicant?.checks ?? [];

  if (verificationScope === 'ALL') {
    return checks;
  }

  return checks.filter(check => check.verificationType === verificationScope);
}

function resolveLatestCheck(data: UserData, verificationScope: VerificationScope) {
  return [...resolveChecks(data, verificationScope)].sort(
    (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
  )[0];
}

function resolveStatus(selectedCheck?: ReturnType<typeof resolveLatestCheck>) {
  if (selectedCheck?.onfidoVerificationStatus) {
    return selectedCheck.onfidoVerificationStatus;
  }

  return 'No checks found';
}

function buildCarSharingRecords(data: UserData, verificationScope: VerificationScope): FeedRecord[] {
  const driversLicenseData = resolveLatestCheck(data, verificationScope);

  if (!driversLicenseData) {
    return [];
  }

  const {
    id = 0,
    validDocumentType,
    validDocumentExpirationDate,
    validIssuingCountry,
    validDateOfBirth,
    documentAuthenticity,
    facialSimilarity,
    watchlist,
  } = driversLicenseData;

  return [
    {
      id: `docType-${id}`,
      content: 'Document type is drivers license',
      icon: getIconType(validDocumentType),
      iconBackground: getIconBackground(validDocumentType),
    },
    {
      id: `expDate-${id}`,
      content: 'Document did not expire',
      icon: getIconType(validDocumentExpirationDate),
      iconBackground: getIconBackground(validDocumentExpirationDate),
    },
    {
      id: `issuingCountry-${id}`,
      content: 'Document issuing country',
      icon: getIconType(validIssuingCountry),
      iconBackground: getIconBackground(validIssuingCountry),
    },
    {
      id: `dob-${id}`,
      content: 'User age is above 21',
      icon: getIconType(validDateOfBirth),
      iconBackground: getIconBackground(validDateOfBirth),
    },
    {
      id: `docAuthenticity-${id}`,
      content: 'Document authenticity',
      icon: getIconType(documentAuthenticity),
      iconBackground: getIconBackground(documentAuthenticity),
    },
    {
      id: `facialSimilarity-${id}`,
      content: 'Facial similarity between document and video',
      icon: getIconType(facialSimilarity),
      iconBackground: getIconBackground(facialSimilarity),
    },
    {
      id: `watchlist-${id}`,
      content: 'User is not politically exposed or appearing on a watchlist',
      icon: getIconType(watchlist),
      iconBackground: getIconBackground(watchlist),
    },
  ];
}

function buildIdentityVerificationRecords(data: UserData, verificationScope: VerificationScope): FeedRecord[] {
  const identityData = resolveLatestCheck(data, verificationScope);

  if (!identityData) {
    return [];
  }

  const { id = 0, documentAuthenticity, facialSimilarity } = identityData;

  return [
    {
      id: `identityDocument-${id}`,
      content: 'Document report is clear',
      icon: getIconType(documentAuthenticity),
      iconBackground: getIconBackground(documentAuthenticity),
    },
    {
      id: `identityFacialSimilarity-${id}`,
      content: 'Facial similarity motion is clear',
      icon: getIconType(facialSimilarity),
      iconBackground: getIconBackground(facialSimilarity),
    },
  ];
}

function resolveVisibleSections(verificationScope: VerificationScope) {
  if (verificationScope === 'ALL') {
    return verificationSections;
  }

  return verificationSections.filter(section => section.type === verificationScope);
}

function Results({ data, verificationScope, selectedVerificationScope, isStale, openRedirectToOnfido }: ResultsProps) {
  const visibleSections = resolveVisibleSections(verificationScope);
  const carSharingEnabled = data.carSharingData?.carSharing?.cccSubscribeStatus ?? false;
  const shouldShowCarSharingPermissions = visibleSections.some(
    section => section.type === VerificationType.CAR_SHARING,
  );
  const firstName = data.onfidoApplicant?.firstName || data.lynkCoApplicant.firstName;
  const lastName = data.onfidoApplicant?.lastName || data.lynkCoApplicant.lastName;
  const applicantName = [firstName, lastName].filter(Boolean).join(' ') || '-';

  function handleOpenRedirectToOnfidoDialog() {
    openRedirectToOnfido();
  }

  function renderSectionHeader(title: string, sectionStatus: string) {
    return (
      <div className="flex justify-between items-center mt-10 mb-5 gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3 className="text-xl">{title}</h3>
          <OnfidoVerificationStatus status={sectionStatus} />
        </div>
        <Button onClick={handleOpenRedirectToOnfidoDialog} variant="white" size="small">
          <Icon name="open_in_new" size="small" padding={0} />
          <span className="pl-2">Open in Onfido</span>
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl mt-10">Results</h3>
      {isStale && (
        <div className="mt-4 mb-2 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Showing results for {getVerificationScopeLabel(verificationScope)}. Submit again to load{' '}
          {getVerificationScopeLabel(selectedVerificationScope)}.
        </div>
      )}
      <Divider />
      <RowDefinitionDetails label="Name">{applicantName}</RowDefinitionDetails>
      <Divider />
      <RowDefinitionDetails label="Signin up in">{getMarketName(data.lynkCoApplicant.marketCode)}</RowDefinitionDetails>
      <Divider />
      <RowDefinitionDetails label="Customer ID">{data.lynkCoApplicant.customerId}</RowDefinitionDetails>
      <Divider />
      <RowDefinitionDetails label="Application start">
        {dayjs.utc(data.lynkCoApplicant.createdAt).format(dateFormat)} UTC
      </RowDefinitionDetails>
      <Divider />
      {visibleSections.map((section, index) => {
        const sectionCheck = resolveLatestCheck(data, section.type);
        const sectionStatus = resolveStatus(sectionCheck);
        const records = section.buildRecords(data, section.type);

        return (
          <div key={section.type}>
            {index > 0 && <Divider />}
            {renderSectionHeader(section.title, sectionStatus)}
            {records.length > 0 && <Feeds records={records} />}
          </div>
        );
      })}
      {shouldShowCarSharingPermissions && (
        <>
          <Divider />
          <h3 className="text-xl mt-10 mb-5">Permissions</h3>
          <Feeds
            records={[
              {
                id: `permissions`,
                content: `Car sharing has ${carSharingEnabled ? 'been' : 'not been'} enabled`,
                icon: getIconType(carSharingEnabled),
                iconBackground: getIconBackground(carSharingEnabled),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

export default Results;
